"use client";

/**
 * Carousel View
 * Renders one date group at a time as a white card (day heading, then the items as rows), with a
 * "Day 1 of 3" counter and outlined previous and next buttons above it.
 */

import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import type { ScheduleItemRow, ScheduleLabelRow } from "../../../../_logic/types/db-types";
import type { ViewerRegistrationKeys } from "../../../../_logic/utils/schedule-registration-match";
import { formatScheduleDayHeading } from "../../../../_logic/utils/schedule-display-format";
import { useEventToday } from "../../../../_hooks/useEventToday";
import { useScheduleLiveMarkers } from "../../../../_hooks/useScheduleLiveMarkers";
import { ScheduleRow, type ScheduleRowConfig } from "./ScheduleRow";

const NO_ITEMS: ScheduleItemRow[] = [];

const ARROW_BUTTON_CLASS =
  "flex h-11 w-11 touch-manipulation items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-800 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 disabled:pointer-events-none disabled:opacity-40";

interface CarouselViewProps {
  dateGroups: { date: string | null; items: ScheduleItemRow[] }[];
  labels: ScheduleLabelRow[];
  primaryColor: string;
  onDelete?: (item: ScheduleItemRow) => void;
  /** When provided, items become clickable (used in public view for detail modal) */
  onItemClick?: (item: ScheduleItemRow) => void;
  /** Page-wide IANA timezone. NULL = floating time. */
  timeZone: string | null;
  isPublic?: boolean;
  /** Viewer's confirmed registrations. `null` = no logged-in attendee. */
  viewerRegistrations: ViewerRegistrationKeys | null;
}

/** Renders schedule items in a paginated carousel with navigation between date groups */
export function CarouselView({
  dateGroups,
  labels,
  primaryColor,
  onDelete,
  onItemClick,
  timeZone,
  isPublic = false,
  viewerRegistrations,
}: CarouselViewProps) {
  // Hooks
  const todayStr = useEventToday(timeZone);

  // States
  const [activeIndex, setActiveIndex] = useState(0);

  // Derived values
  // Clamp, since a label filter can shrink the day list under a later index.
  const safeIndex = Math.min(activeIndex, Math.max(0, dateGroups.length - 1));
  const activeGroup = dateGroups[safeIndex];
  const isActiveToday = activeGroup?.date != null && activeGroup.date === todayStr;
  const { liveItemIds, upcomingMinutesById } = useScheduleLiveMarkers(
    isActiveToday ? activeGroup.items : NO_ITEMS,
    timeZone
  );
  const rowConfig = useMemo<ScheduleRowConfig>(
    () => ({ labels, primaryColor, onDelete, onItemClick, isPublic, viewerRegistrations, surface: "card" }),
    [labels, primaryColor, onDelete, onItemClick, isPublic, viewerRegistrations]
  );

  // Render
  if (!activeGroup) return null;
  const heading = activeGroup.date ? formatScheduleDayHeading(activeGroup.date) : "Anytime";

  return (
    <div className="flex flex-col gap-4">
      {dateGroups.length > 1 && (
        <div className="flex items-center justify-between gap-4">
          <span aria-live="polite" className="text-sm font-semibold text-(--lp-text,#404040)">
            Day {safeIndex + 1} of {dateGroups.length}
          </span>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => setActiveIndex(Math.max(0, safeIndex - 1))}
              disabled={safeIndex === 0}
              className={ARROW_BUTTON_CLASS}
              aria-label="Previous day"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setActiveIndex(Math.min(dateGroups.length - 1, safeIndex + 1))}
              disabled={safeIndex === dateGroups.length - 1}
              className={ARROW_BUTTON_CLASS}
              aria-label="Next day"
            >
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      <article
        aria-label={heading}
        className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white px-5 pb-2 pt-5 sm:px-7 sm:pt-7"
      >
        <h3 className="text-2xl font-bold tracking-tight text-(--lp-accent-on-card,#4338ca)">{heading}</h3>
        <ul className="flex list-none flex-col divide-y divide-gray-100 border-t border-gray-100">
          {activeGroup.items.map((item) => (
            <li key={item.id}>
              <ScheduleRow
                config={rowConfig}
                item={item}
                isLive={liveItemIds.has(item.id)}
                startsInMinutes={upcomingMinutesById.get(item.id)}
              />
            </li>
          ))}
        </ul>
      </article>
    </div>
  );
}
